/**
 * ============================================================================
 * MessageManagementPage — 다국어 메시지 관리 (V22 vertical 정규화)
 * ============================================================================
 *
 * 【역할】
 *   알림/경고/확인 메시지(fw_message) 마스터 CRUD.
 *   V22 부터 locale 별 row 수직(vertical) 정규화 — 한 messageId × N locale.
 *   기본 메시지는 회색 텍스트로 함께 표시하여 번역 가이드 제공.
 *
 * 【주요 기능】
 *   - 검색: keyword (messageId/내용/설명)
 *   - CRUD: 인라인 편집(messageId/locale/value/useYn) + 벌크 저장
 *   - messageType 컬러 Tag: info(blue) / success(green) / warn(orange) / error(red) / confirm(purple)
 *   - locale tick 리렌더 (useI18nTickStore) — 시스템 locale 변경 시 자동 갱신
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=message/searchListVertical) — V22 vertical 조회
 *   - POST /api/v1/transaction (service=message/saveVertical)       — V23 vertical 저장
 *
 * 【DataSet/Hook】
 *   - useDataSet<MsgRow>()    — ds_messageList : 메시지 목록 (PK: messageId+locale)
 *   - useTransaction()        — RPC 통합 호출
 *   - useLabel()              — i18n 라벨(t)
 *   - useI18nTickStore()      — locale 변경 tick 구독 (리렌더 트리거)
 *   - useSupportedLocales()   — locale 드롭다운 옵션
 *
 * 【사용처】 router/index.tsx — `{ path: '/message', element: <MessageManagementPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageMessageManagement.vue`
 * 【의존성】 antd Form/Input/Select/Tag, framework hooks (RowStateTag)
 */
import { useEffect, useMemo, useState } from 'react'
import { Form, Input, Select, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormInput from '@/components/common/FormInput'
import { useDataSet, useTransaction } from '@/framework'
import type { TrackedRow } from '@/framework/useDataSet'
import { useLabel } from '@/framework/useLabel'
import RowStateTag from '@/components/common/RowStateTag'
import { useI18nTickStore, useSupportedLocales } from '@/framework/useLocale'

/** 메시지 1행 — V22 vertical(locale 별) + 구 평면화(textKo/En/...) 호환. */
interface MsgRow {
  /** 메시지 ID PK 일부 (예: "MSG_SAVE_SUCCESS"). */
  messageId: string
  /** 메시지 코드 (서버 별칭, messageId 와 동일 사용 가능). */
  code?: string
  /** 메시지 유형 — UI 컬러/아이콘 결정. 대문자 호환. */
  messageType: 'info' | 'success' | 'warn' | 'error' | 'confirm' | 'INFO' | 'WARN' | 'ERROR'
  /** 로케일 코드 (예: "ko"·"en"). PK 일부. */
  locale?: string
  /** 로케일 표시명 (서버 join). */
  localeName?: string
  /** 국기 이모지 (서버 join). */
  flagEmoji?: string
  /** 해당 locale 의 메시지 텍스트 (실제 편집 대상). */
  value?: string
  /** 기본 locale 의 메시지 (회색 텍스트, 편집 불가). */
  defaultMessage?: string
  /** 메시지 설명/사용처 메모 (운영자용). */
  description?: string
  /** 사용여부 ('Y'=노출 / 'N'=숨김). */
  useYn: 'Y' | 'N'
  // (구) 평면화 호환 — V21 이전 데이터 호환용
  /** [legacy] 한국어. */
  textKo?: string
  /** [legacy] 영어. */
  textEn?: string
  /** [legacy] 중국어. */
  textZh?: string
  /** [legacy] 일본어. */
  textJa?: string
  /** [legacy] 베트남어. */
  textVi?: string
}

/**
 * MessageManagementPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function MessageManagementPage() {
  const ds = useDataSet<MsgRow>()
  const { transaction } = useTransaction()
  const { t } = useLabel()
  useI18nTickStore((s) => s.tick)
  const supportedLocales = useSupportedLocales()
  const localeOptions = useMemo(
    () =>
      supportedLocales.map((l) => ({
        value: l.locale,
        label: `${l.flagEmoji ?? ''} ${l.locale.toUpperCase()} · ${l.nativeName ?? l.name ?? l.locale}`
      })),
    [supportedLocales]
  )
  const [searchForm] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)

  /**
   * 메시지 검색 — message/searchListVertical → ds.
   * @param values 외부 폼 값 (생략 시 내부 searchForm)
   * loading: false 옵션으로 전역 로딩 GIF 비활성화 (자체 isLoading state 사용).
   */
  const search = async (values?: Record<string, unknown>) => {
    setIsLoading(true)
    try {
      await transaction({
        id: 'select',
        // V22: locale 별 row 정규화 (수직 형태)
        service: 'message/searchListVertical',
        inds: { ds_search: values ?? searchForm.getFieldsValue() },
        outds: { ds_messageList: ds },
        loading: false
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    search()
  }, [])

  /** 신규 행 추가 — messageType 기본 'info', useYn 'Y'. */
  const add = () =>
    ds.addRow({
      messageId: '',
      messageType: 'info',
      useYn: 'Y',
      textKo: '',
      textEn: '',
      textZh: '',
      textJa: '',
      textVi: ''
    } as MsgRow)
  /** 선택행 삭제 — DataSet C/U/D 추적. */
  const del = () => ds.selectedIndex >= 0 && ds.deleteRow(ds.selectedIndex)

  /** 벌크 저장 — message/saveVertical (vertical 단위 C/U/D). 성공 시 재조회. */
  const save = async () => {
    setIsLoading(true)
    try {
      const res = await transaction({
        id: 'save',
        // V23: vertical 형태 (code + locale + value) 단위 저장
        service: 'message/saveVertical',
        action: 'save',
        inds: { ds_messageList: ds },
        outds: { ds_messageList: ds },
        loading: false
      })
      if (res.success) await search()
    } finally {
      setIsLoading(false)
    }
  }

  /** 셀 단위 단일 필드 갱신 헬퍼 — DataSet 자동 _state='U' 마킹. */
  const patch = (idx: number, key: keyof MsgRow, v: unknown) =>
    ds.updateRow(idx, { [key]: v } as Partial<MsgRow>)

  // V22: locale 별 row 정규화 — 수직 형태
  const TYPE_COLOR: Record<string, string> = {
    INFO: 'blue',
    WARN: 'orange',
    ERROR: 'red',
    info: 'blue',
    success: 'green',
    warn: 'orange',
    error: 'red',
    confirm: 'purple'
  }
  const columns: ColumnsType<TrackedRow<MsgRow>> = [
    {
      title: t('COL_MESSAGE_ID', '메시지ID'),
      width: 240,
      render: (_v, r, i) => (
        <Input
          size="small"
          value={r.data.messageId}
          disabled={r._state !== 'C'}
          onChange={(e) => patch(i, 'messageId', e.target.value)}
        />
      )
    },
    {
      title: t('COL_MESSAGE_TYPE', '유형'),
      width: 100,
      render: (_v, r) => (
        <Tag color={TYPE_COLOR[r.data.messageType || ''] || 'default'}>
          {String(r.data.messageType || '').toUpperCase()}
        </Tag>
      )
    },
    {
      title: t('COL_LOCALE', '로케일'),
      width: 200,
      render: (_v, r, i) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={r.data.locale}
          options={localeOptions}
          onChange={(v) => patch(i, 'locale', v)}
        />
      )
    },
    {
      title: t('COL_VALUE', '값'),
      width: 360,
      render: (_v, r, i) => (
        <Input
          size="small"
          value={r.data.value}
          onChange={(e) => patch(i, 'value', e.target.value)}
        />
      )
    },
    {
      title: t('COL_DEFAULT_MESSAGE', '기본 메시지'),
      width: 220,
      render: (_v, r) => <span style={{ color: '#999' }}>{r.data.defaultMessage}</span>
    },
    {
      title: t('COL_USE_YN', '사용'),
      width: 90,
      render: (_v, r, i) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={r.data.useYn}
          options={[
            { label: t('LBL_YES', '사용'), value: 'Y' },
            { label: t('LBL_NO', '미사용'), value: 'N' }
          ]}
          onChange={(v) => patch(i, 'useYn', v)}
        />
      )
    },
    {
      title: t('COL_STATUS', '상태'),
      dataIndex: '_state',
      width: 70,
      render: (s) => <RowStateTag state={s} />
    }
  ]

  return (
    <AppPage
      search={{
        form: searchForm,
        onSearch: () => search(),
        content: (
          <Form.Item label={t('LBL_KEYWORD', '검색어')} name="keyword">
            <FormInput placeholder={t('PH_MESSAGE_SEARCH', '메시지ID/내용/설명')} />
          </Form.Item>
        )
      }}
      toolbar={{
        onSearch: () => search(),
        onAdd: add,
        onDelete: del,
        onSave: save,
        dirty: ds.isDirty
      }}
    >
      <DataTable<TrackedRow<MsgRow>>
        rowKey="_rowId"
        dataSource={ds.getVisibleRows()}
        columns={columns}
        loading={isLoading}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: ds.selectedRow ? [ds.selectedRow._rowId] : [],
          onChange: (keys) => {
            const idx = ds.rows.findIndex((r) => r._rowId === keys[0])
            ds.setSelectedIndex(idx)
          }
        }}
      />
    </AppPage>
  )
}
